import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NavItem, NAVIGATION_DATA } from '../layouts/navigation/navigation-data';
import { DashboardComponent } from '../components/dashboard/dashboard.component';
import { ComponentType } from '@angular/cdk/portal';
import { Tab } from '@interfaces';

interface SavedTab {
  id: string;
  title: string;
  icon: string;
  state: any;
  path: string;
  parent: string;
}

@Injectable({
  providedIn: 'root',
})
export class TabService {
  private readonly STORAGE_KEY = 'open_tabs';
  private readonly SELECTED_TAB_KEY = 'selected_tab';

  private tabsSubject = new BehaviorSubject<SavedTab[]>(this.loadTabs());
  tabs$ = this.tabsSubject.asObservable();

  private selectedTabSubject = new BehaviorSubject<number>(this.loadSelectedTab());
  selectedTab$ = this.selectedTabSubject.asObservable();

  constructor() { }

  /** ✅ Load Tabs and Restore Components */
  private loadTabs(): SavedTab[] {
    const savedTabs = sessionStorage.getItem(this.STORAGE_KEY);
    return savedTabs ? JSON.parse(savedTabs) : [];
  }

  private loadSelectedTab(): number {
    return JSON.parse(sessionStorage.getItem(this.SELECTED_TAB_KEY) || '0');
  }

  /** ✅ Convert `Tab[]` to `SavedTab[]` for session storage */
  saveTabs(tabs: any[]) {
    const savedTabs: SavedTab[] = tabs.map(({ id, title, icon, state, path, parent }) => ({ id, title, icon, state, path, parent }));
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(savedTabs));
    this.tabsSubject.next(savedTabs);
  }

  /** ✅ Convert `SavedTab[]` back to `Tab[]` */
  restoreTabs(savedTabs: SavedTab[]): any[] {
    return savedTabs.map(tab => ({
      ...tab,
      component: this.getComponentByTab(tab) // Reattach components dynamically
    }));
  }

  saveSelectedTab(index: number) {
    sessionStorage.setItem(this.SELECTED_TAB_KEY, JSON.stringify(index));
    this.selectedTabSubject.next(index);
  }

  /** ✅ Restore component from title */
  private getComponentByTab(tab: SavedTab): any {
    if (tab.parent) {
      const navParent = NAVIGATION_DATA.filter(n => n.title === tab.parent)[0];
      const item: NavItem | undefined = navParent.children?.find((navItem: NavItem) => navItem.title == tab.title);
      return item ? item.component : DashboardComponent;
    }
    else {
      return DashboardComponent;
    }
  }
  saveState(title: string, state: any) {
    sessionStorage.setItem(`state-${title}`, JSON.stringify(state));
  }

  loadState(title: string): any {
    return JSON.parse(sessionStorage.getItem(`state-${title}`) || '{}');
  }
}
